<template>
  <div class="wrapper">
    <div class="table-wrapper">
      <div
        class="table-caption"
        v-if="isEdit"
      >
        {{ $t('lease.linkedLeases') }}
      </div>
      <table>
        <thead>
          <tr>
            <th
              v-for="(header, index) in itemHeaders"
              @click="sortBy(header.code)"
              :class="`sort_header ${header.name}`"
              v-if="header.code !== 'leaseEnd'"
              :key="index"
            >
              {{ header.name }} {{ orderbyAttr === header.code ? reverse ? '&#9660;' : '&#9650;' : null }}
            </th>
            <th v-if="isEdit" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in linkedLeases"
            :key="index"
          >
            <td>{{ item.id }}</td>
            <td>{{ item.premises }}</td>
            <td>{{ item.buildAbbr }}</td>
            <td>{{ $t(`lease.leaseName.${item.leaseTypeDesc}`) }}</td>
            <td>{{ item.tradeName }}</td>
            <td>{{ formatYN(item.active) }}</td>
            <td>{{ item.leaseStart && dateUtil.formatDate(item.leaseStart) || '--' }}</td>
            <td
              class="icon-remove"
              v-if="isEdit && isCurrentCompanyLease(item.id)"
            >
              <i
                class="fa fa-times-thin"
                @click="removeFromTenancy(item)"
              />
            </td>
            <td v-else-if="isEdit" />
          </tr>
        </tbody>
      </table>
    </div>
    <div
      class="table-wrapper"
      v-if="isEdit"
    >
      <div class="table-caption">
        {{ $t('lease.allCompanyLeases') }}
      </div>
      <table>
        <thead>
          <tr>
            <th />
            <th
              v-for="(header, index) in itemHeaders"
              @click="sortAllBy(header.code)"
              :class="`sort_header ${header.class}`"
              :key="index"
            >
              {{ header.name }} {{ orderAllByAttr === header.code ? reverseAll ? '&#9660;' : '&#9650;' : null }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in companyLeases"
            :key="index"
          >
            <td>
              <input
                type="checkbox"
                :key="`lease-key-${leaseKey}`"
                :disabled="isChecked(item)"
                :class="{'disable-checkbox': isChecked(item)}"
                class="checkbox"
                :checked="isChecked(item)"
                @change="event => addToTenancy(item, event.target.checked)"
              >
            </td>
            <td>{{ item.id }}</td>
            <td>{{ item.premises }}</td>
            <td>{{ item.buildAbbr }}</td>
            <td>{{ $t(`lease.leaseName.${item.leaseTypeDesc}`) }}</td>
            <td>{{ item.tradeName }}</td>
            <td>{{ formatYN(item.active) }}</td>
            <td>{{ item.leaseStart && dateUtil.formatDate(item.leaseStart) || '--' }}</td>
            <td>{{ item.leaseEnd && dateUtil.formatDate(item.leaseEnd) || '--' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <Modal
      modalId="confirm-link-modal"
      ref="confirmLinkModal"
      :showCloseBtn="false"
    >
      <div
        class="modal-pure-text"
        slot="content"
      >
        {{ $t('tenancy.unmatched.warning') }}
      </div>
      <div slot="btns">
        <button
          id="confirm_no"
          type="button"
          class="btn btn--darkblue"
          @click="cancelAddLease()"
        >{{ $t('action.cancel') }}</button>
        <button
          id="confirm_yes"
          type="button"
          class="btn btn--orange"
          @click="doAddLease()"
        >{{ $t('action.continue') }}</button>
      </div>
    </Modal>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import { TenancyLeaseTypes } from '@/utils/locale'
import { sortLeaseWithType } from '@/modules/ccd/tenancy/utils'
import mutationTypes from '@/store/mutation-types'
import lodash from 'lodash'
import dateUtil from '@/utils/dateUtil'
import Modal from '@/controls/Modal'

export default {
  name: 'LeaseInformation',
  components: {
    Modal,
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      reverse: 0,
      reverseAll: 0,
      orderbyAttr: 'disabled',
      orderAllByAttr: 'disabled',
      dateUtil,
      leaseToAdd: null,
      leaseToAddIndex: -1,
      confirmLinkModal: null,
      leaseKey: 1,
    }
  },
  methods: {
    ...mapMutations('ccd', {
      addLeaseToTenancy: mutationTypes.TENANCY_ADD_LEASE,
      removeLeaseFromTenancy: mutationTypes.TENANCY_REMOVE_LEASE,
      updateLeaseTenancyId: mutationTypes.TENANCY_UPDATE_COMPANY_LEASE,
      updateTenancyPathField: mutationTypes.TENANCY_UPDATE_PATH_FIELD,
    }),
    formatYN (active) {
      return this.$options.filters.customYNLabel(active ? 'Yes' : 'No')
    },
    isChecked (item) {
      return this.linkedLeaseIds.includes(item.id)
    },
    doAddLease () {
      if (this.leaseToAdd) {
        this.addLeaseToTenancy({item: this.leaseToAdd})
        if (this.currentTenancy.id) {
          this.updateLeaseTenancyId({
            index: this.leaseToAddIndex,
            path: ['tenancyId'],
            newValue: this.currentTenancy.id,
          })
        }
      }
      this.leaseToAdd = null
      this.leaseToAddIndex = -1
      this.closeConfirmLinkModal()
    },
    isCurrentCompanyLease (id) {
      return this.companyLeaseIds.includes(id)
    },
    cancelAddLease () {
      this.leaseKey += 1
      this.closeConfirmLinkModal()
    },
    addToTenancy (lease, checked) {
      const indexInCompanyLeases = (deep(this.currentTenancy, 'company.leases') || [])
        .findIndex(item => item.id === lease.id)
      if (checked) {
        this.leaseToAdd = lease
        this.leaseToAddIndex = indexInCompanyLeases
        const buildingUnmatched = lease.building !== deep(this.currentTenancy, 'building.code')
        const typeUnmatched = !TenancyLeaseTypes[this.currentTenancy.type].includes(lease.leaseType)
        if (buildingUnmatched || typeUnmatched) {
          this.openConfirmLinkModal()
        } else {
          this.doAddLease()
        }
      } else {
        this.updateLeaseTenancyId({
          index: indexInCompanyLeases,
          path: ['tenancyId'],
          newValue: null,
        })

        const indexInLeases = (deep(this.currentTenancy, 'leases') || [])
          .findIndex(item => item.id === lease.id)
        this.removeLeaseFromTenancy({index: indexInLeases})
      }
    },
    removeFromTenancy (lease) {
      const indexInLeases = (deep(this.currentTenancy, 'leases') || [])
        .findIndex(item => item.id === lease.id)
      this.removeLeaseFromTenancy({index: indexInLeases})

      const indexInCompanyLeases = (deep(this.currentTenancy, 'company.leases') || [])
        .findIndex(item => item.id === lease.id)
      this.updateLeaseTenancyId({
        index: indexInCompanyLeases,
        path: ['tenancyId'],
        newValue: null,
      })
    },
    sortBy (attr) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
    },
    sortAllBy (attr) {
      if (this.orderAllByAttr === attr) {
        this.reverseAll = !this.reverseAll
      }
      this.orderAllByAttr = attr
    },
    async initConfirmLinkModal () {
      this.confirmLinkModal = this.$refs.confirmLinkModal
    },
    closeConfirmLinkModal () {
      this.confirmLinkModal && this.confirmLinkModal.close()
    },
    openConfirmLinkModal () {
      this.confirmLinkModal && this.confirmLinkModal.open()
    },
    sortFunc (obj, orderbyAttr) {
      if (orderbyAttr === 'id') {
        return parseInt(obj[orderbyAttr], 10)
      } else if (['leaseStart', 'leaseEnd'].includes(orderbyAttr)) {
        return obj[orderbyAttr] ? (new Date(obj[orderbyAttr])).getTime() : Number.MIN_SAFE_INTEGER
      } else {
        return obj[orderbyAttr]
      }
    },
  },
  async mounted () {
    await this.initConfirmLinkModal()
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    itemHeaders () {
      return [{
        code: 'id',
        name: this.$t('lease.leaseNumber'),
      }, {
        code: 'premises',
        name: this.$t('lease.premises'),
      }, {
        code: 'buildAbbr',
        name: this.$t('tenancy.building'),
      }, {
        code: 'leaseTypeDesc',
        name: this.$t('lease.leaseType'),
      }, {
        code: 'tradeName',
        name: this.$t('lease.tradeName'),
      }, {
        code: 'active',
        name: this.$t('lease.active'),
      }, {
        code: 'leaseStart',
        name: this.$t('lease.startDate'),
      }, {
        code: 'leaseEnd',
        name: this.$t('lease.endDate'),
      },
      ]
    },
    linkedLeases () {
      const leases = deep(this.currentTenancy, 'leases') || []
      if (this.orderbyAttr === 'disabled') {
        return sortLeaseWithType(leases)
      }
      const result = lodash.orderBy(leases, (obj) => this.sortFunc(obj, this.orderbyAttr))
      return this.reverse ? result.reverse() : result
    },
    linkedLeaseIds () {
      return this.linkedLeases.map(lease => lease.id)
    },
    companyLeases () {
      const allLeases = (deep(this.currentTenancy, 'company.leases') || [])
      if (this.orderAllByAttr === 'disabled') {
        return sortLeaseWithType(allLeases)
      }

      const result = lodash.orderBy(allLeases, (obj) => this.sortFunc(obj, this.orderAllByAttr))
      return this.reverseAll ? result.reverse() : result
    },
    companyLeaseIds () {
      return this.companyLeases.map(lease => lease.id)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/icon_remove';

.table-caption {
  text-align: left;
  font-size: 16px;
  padding-left: 0px;
  font-weight: 700;
  margin-bottom: 15px;
}
.disable-checkbox {
  border: 1px solid grey;
  background-color: #d8d8d8;
  cursor: not-allowed;
}

.checkbox {
  cursor: pointer;
  cursor: hand;
}

.sort_header {
  cursor:pointer
}
</style>

<style lang="scss">
@import '~styles/popup';
</style>
